/**
 * GroupTypesField component for displaying group types from custom enums with edit link
 * Used specifically in billing settings to show configured group types
 */

import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Pencil } from "lucide-react";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import type { FieldConfig } from "../types";

interface GroupTypesFieldProps<T> {
  field: FieldConfig<T>;
  value: string[];
  onChange?: (value: string[]) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
}

export function GroupTypesField<T>({
  field,
  id,
  placeholder = "No group types configured",
  "aria-describedby": ariaDescribedBy,
}: GroupTypesFieldProps<T>) {
  const { i18n } = useLingui();

  // Fetch group types using the same approach as TariffVersionFormWithEnums
  const { options: groupTypes, isLoading } = useEnumOptions('groups', 'type');

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground" id={id} aria-describedby={ariaDescribedBy}>
        {i18n._(t`Loading group types...`)}
      </div>
    );
  }

  return (
    <div id={id} className="space-y-2" aria-describedby={ariaDescribedBy}>
      <div className="flex items-center justify-between">
        {groupTypes && groupTypes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {groupTypes.map((type) => (
              <Badge key={type.value} variant="secondary">
                {type.label}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {placeholder || field.placeholder || i18n._(t`No group types configured`)}
          </div>
        )}
        <Link
          to="/settings/customization?object=groups"
          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors ml-2"
          title={i18n._(t`Edit group types`)}
        >
          <Pencil className="h-3 w-3" />
          <span>{i18n._(t`Edit`)}</span>
        </Link>
      </div>
    </div>
  );
}